import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  MapLayerType,
  ViewportState,
  BoundingBox,
} from '@/types/map';

// Cluster mode options
export type ClusterMode = 'default' | 'severity' | 'status' | 'layer';

// Heatmap layer types
export type HeatmapLayerType = 'damage' | 'resources' | 'tasks';

// Continental-US default viewport (last-resort initial extent)
export const DEFAULT_VIEWPORT: ViewportState = {
  center: [-98.5795, 39.8283],
  zoom: 4,
};

// Zoom used when centering the map on an engaged incident
export const INCIDENT_ENGAGED_ZOOM = 10;

// Epsilon for default-viewport comparison. The debounced bounds writer round-
// trips the center through ArcGIS/Web Mercator math, so a viewport that never
// moved can come back with float drift (observed in production:
// 39.828300000000006 vs 39.8283). ~1e-6 degrees is ~10cm — far below any
// intentional pan, far above accumulated float noise.
const DEFAULT_VIEWPORT_EPSILON = 1e-6;

export function isDefaultViewport(viewport: ViewportState): boolean {
  return (
    Math.abs(viewport.center[0] - DEFAULT_VIEWPORT.center[0]) < DEFAULT_VIEWPORT_EPSILON &&
    Math.abs(viewport.center[1] - DEFAULT_VIEWPORT.center[1]) < DEFAULT_VIEWPORT_EPSILON &&
    Math.abs(viewport.zoom - DEFAULT_VIEWPORT.zoom) < DEFAULT_VIEWPORT_EPSILON
  );
}

/**
 * A persisted viewport "belongs" to the engaged incident only when it was
 * stamped with that incident AND it is a real (non-default) viewport. The
 * continental-US default can never satisfy incident-viewport precedence,
 * even if a stale stamp says otherwise — a default view is never an
 * intentional incident view.
 */
export function persistedViewportBelongsToIncident(
  viewport: ViewportState,
  viewportIncidentId: string | null,
  engagedIncidentId: string | null
): boolean {
  return (
    engagedIncidentId !== null &&
    viewportIncidentId === engagedIncidentId &&
    !isDefaultViewport(viewport)
  );
}

/**
 * Coerce incident center coordinates to finite numbers.
 * pg NUMERIC columns arrive as strings over JSON, so Number() coercion is
 * required — plain Number.isFinite on the raw value silently rejects them.
 */
export function coerceIncidentCenter(
  lat: unknown,
  lng: unknown
): { lat: number; lng: number } | null {
  if (lat === null || lat === undefined || lat === '' || lng === null || lng === undefined || lng === '') {
    return null;
  }
  const nLat = Number(lat);
  const nLng = Number(lng);
  if (!Number.isFinite(nLat) || !Number.isFinite(nLng)) return null;
  if (nLat < -90 || nLat > 90 || nLng < -180 || nLng > 180) return null;
  return { lat: nLat, lng: nLng };
}

/**
 * Extract a representative center point from a GeoJSON value (bare geometry,
 * Feature, FeatureCollection, or GeometryCollection): the bounding-box center
 * of every valid coordinate position found. Tolerant of loose shapes — the
 * incident record's `area_of_operations` is typed as a loose GeoJSON object.
 * Returns null when no valid position exists.
 */
export function geoJsonCenter(geo: unknown): { lat: number; lng: number } | null {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  let found = false;

  const visitCoordinates = (node: unknown): void => {
    if (!Array.isArray(node)) return;
    // A GeoJSON Position is [lng, lat, alt?] — numbers at the leaf level.
    if (typeof node[0] === 'number' && typeof node[1] === 'number') {
      const lng = node[0];
      const lat = node[1];
      if (
        Number.isFinite(lng) &&
        Number.isFinite(lat) &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180
      ) {
        found = true;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
      }
      return;
    }
    for (const child of node) visitCoordinates(child);
  };

  const visit = (node: unknown): void => {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      for (const child of node) visit(child);
      return;
    }
    const obj = node as Record<string, unknown>;
    if (obj.coordinates !== undefined) visitCoordinates(obj.coordinates);
    if (obj.geometry !== undefined) visit(obj.geometry);
    if (obj.geometries !== undefined) visit(obj.geometries);
    if (obj.features !== undefined) visit(obj.features);
  };

  visit(geo);
  if (!found) return null;
  return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
}

/** Minimal slice of the incident record needed to resolve a centering point. */
export interface IncidentCenterSource {
  center_lat?: number | string | null;
  center_lng?: number | string | null;
  area_of_operations?: unknown;
}

/**
 * Resolve an engaged incident's centering point from the ACTUAL record shape:
 * 1. Explicit `center_lat` / `center_lng` columns — but these are OPTIONAL on
 *    the incidents table and frequently NULL.
 * 2. Fallback: bbox center of the `area_of_operations` GeoJSON. This is the
 *    same geometry the unified-map incidents layer renders from
 *    (`SELECT area_of_operations as geometry ... WHERE area_of_operations IS
 *    NOT NULL`), so any incident that appears on the map can be centered even
 *    when its center columns are NULL. (Weather's useWeatherLocation does the
 *    equivalent centroid fallback — the map must match.)
 */
export function resolveIncidentCenter(
  incident: IncidentCenterSource | null | undefined
): { lat: number; lng: number } | null {
  if (!incident) return null;
  const explicit = coerceIncidentCenter(incident.center_lat, incident.center_lng);
  if (explicit) return explicit;
  return geoJsonCenter(incident.area_of_operations);
}

export interface InitialViewportInputs {
  /** Explicit lat/lng URL params present (highest precedence) */
  urlHasViewport: boolean;
  /** Current store viewport (rehydrated from localStorage / URL restore) */
  viewport: ViewportState;
  /** Incident that was engaged when the viewport was last persisted */
  viewportIncidentId: string | null;
  /** Currently engaged incident id (null when in daily-ops mode) */
  engagedIncidentId: string | null;
  /** Engaged incident's coordinates, if it has any */
  incidentCenter: { lat: number; lng: number } | null;
}

export type InitialViewportSource = 'url' | 'persisted' | 'incident' | 'default';

/**
 * Resolve the map's initial viewport. Precedence (highest wins):
 * 1. Explicit lat/lng/zoom URL params (already applied to the store viewport).
 * 2. Persisted viewport, IF it was persisted while the SAME incident was
 *    engaged (a persisted viewport belonging to a different/no incident is
 *    superseded by the engaged incident's center).
 * 3. Engaged incident's coordinates at INCIDENT_ENGAGED_ZOOM.
 * 4. Continental-US default.
 */
export function resolveInitialViewport(
  inputs: InitialViewportInputs
): { source: InitialViewportSource; viewport: ViewportState } {
  const { urlHasViewport, viewport, viewportIncidentId, engagedIncidentId, incidentCenter } = inputs;

  if (urlHasViewport) {
    return { source: 'url', viewport };
  }

  if (engagedIncidentId && incidentCenter) {
    if (!persistedViewportBelongsToIncident(viewport, viewportIncidentId, engagedIncidentId)) {
      return {
        source: 'incident',
        viewport: {
          center: [incidentCenter.lng, incidentCenter.lat],
          zoom: INCIDENT_ENGAGED_ZOOM,
        },
      };
    }
  }

  if (!isDefaultViewport(viewport)) {
    return { source: 'persisted', viewport };
  }

  return { source: 'default', viewport };
}

interface MapUIState {
  // Feature selection
  selectedFeatureId: string | null;
  selectedFeatureLayer: MapLayerType | null;

  // Panel state
  isFilterPanelOpen: boolean;
  isLayerPanelOpen: boolean;
  isExportDialogOpen: boolean;

  // Onboarding tour
  isMapTourActive: boolean;
  hasCompletedMapTour: boolean;

  // Accessibility
  isHighContrastMode: boolean;

  // Collaboration: follow user mode
  followingUserId: string | null;
  followingUserName: string | null;

  // Context menu state (desktop right-click)
  contextMenu: { x: number; y: number; lat: number; lng: number } | null;

  // Historical playback mode
  isHistoricalMode: boolean;
  historicalTimestamp: string | null;

  // Viewport (persisted)
  viewport: ViewportState;
  // Incident that was engaged when the viewport was last persisted (null =
  // no incident / unknown). Used to decide whether a persisted viewport
  // should win over the engaged incident's center on map open.
  viewportIncidentId: string | null;

  // Coordinate display format
  coordinateFormat: 'decimal' | 'dms';

  // Clustering state
  clusterMode: ClusterMode;
  isSpiderfierEnabled: boolean;

  // 3D terrain state
  isTerrainEnabled: boolean;
  terrainExaggeration: number; // 0.5 to 3.0
  isBuildingsExtrusionEnabled: boolean;

  // Heatmap state
  isDamageHeatmapVisible: boolean;
  isResourceHeatmapVisible: boolean;
  isTaskHeatmapVisible: boolean;
  heatmapOpacity: number; // 0-1

  // Radar animation state (UI-only, not layer config)
  isRadarAnimating: boolean;
  radarCurrentFrame: number;

  // Actions
  selectFeature: (id: string | null, layer: MapLayerType | null) => void;
  toggleFilterPanel: () => void;
  toggleLayerPanel: () => void;
  setExportDialogOpen: (open: boolean) => void;
  startMapTour: () => void;
  completeMapTour: () => void;
  toggleHighContrastMode: () => void;
  setFollowingUser: (userId: string | null, userName: string | null) => void;
  setContextMenu: (menu: { x: number; y: number; lat: number; lng: number } | null) => void;
  setHistoricalMode: (active: boolean, timestamp?: string | null) => void;
  setHistoricalTimestamp: (timestamp: string | null) => void;
  setViewport: (viewport: Partial<ViewportState>, engagedIncidentId?: string | null) => void;
  setBounds: (bounds: BoundingBox) => void;
  applyTenantDefaultExtent: (centerLat?: number, centerLng?: number, zoom?: number) => void;
  setCoordinateFormat: (format: 'decimal' | 'dms') => void;
  toggleCoordinateFormat: () => void;
  setClusterMode: (mode: ClusterMode) => void;
  toggleSpiderfier: () => void;
  toggleTerrain: () => void;
  setTerrainExaggeration: (exaggeration: number) => void;
  toggleBuildingsExtrusion: () => void;
  toggleDamageHeatmap: () => void;
  toggleResourceHeatmap: () => void;
  toggleTaskHeatmap: () => void;
  setHeatmapOpacity: (opacity: number) => void;
  toggleRadarAnimation: () => void;
  setRadarCurrentFrame: (frame: number) => void;
  resetUIState: () => void;
}

const INITIAL_UI_STATE = {
  selectedFeatureId: null as string | null,
  selectedFeatureLayer: null as MapLayerType | null,
  isFilterPanelOpen: false,
  isLayerPanelOpen: false,
  isExportDialogOpen: false,
  isMapTourActive: false,
  hasCompletedMapTour: false,
  isHighContrastMode: false,
  followingUserId: null as string | null,
  followingUserName: null as string | null,
  contextMenu: null as { x: number; y: number; lat: number; lng: number } | null,
  isHistoricalMode: false,
  historicalTimestamp: null as string | null,
  viewport: { ...DEFAULT_VIEWPORT, center: [...DEFAULT_VIEWPORT.center] as [number, number] } as ViewportState,
  viewportIncidentId: null as string | null,
  coordinateFormat: 'decimal' as const,
  clusterMode: 'default' as ClusterMode,
  isSpiderfierEnabled: true,
  isTerrainEnabled: false,
  terrainExaggeration: 1.5,
  isBuildingsExtrusionEnabled: false,
  isDamageHeatmapVisible: false,
  isResourceHeatmapVisible: false,
  isTaskHeatmapVisible: false,
  heatmapOpacity: 0.7,
  isRadarAnimating: false,
  radarCurrentFrame: 0,
};

export const useMapUIStore = create<MapUIState>()(
  persist(
    (set) => ({
      ...INITIAL_UI_STATE,

      selectFeature: (id, layer) => set({ selectedFeatureId: id, selectedFeatureLayer: layer }),
      // Toggle filter panel (closes layer panel to prevent overlap)
      toggleFilterPanel: () =>
        set((state) => ({
          isFilterPanelOpen: !state.isFilterPanelOpen,
          isLayerPanelOpen: state.isFilterPanelOpen ? state.isLayerPanelOpen : false,
        })),
      // Toggle layer panel (closes filter panel to prevent overlap)
      toggleLayerPanel: () =>
        set((state) => ({
          isLayerPanelOpen: !state.isLayerPanelOpen,
          isFilterPanelOpen: state.isLayerPanelOpen ? state.isFilterPanelOpen : false,
        })),
      setExportDialogOpen: (open) => set({ isExportDialogOpen: open }),
      startMapTour: () => set({ isMapTourActive: true }),
      completeMapTour: () => set({ isMapTourActive: false, hasCompletedMapTour: true }),
      toggleHighContrastMode: () => set((state) => ({ isHighContrastMode: !state.isHighContrastMode })),
      setFollowingUser: (userId, userName) => set({ followingUserId: userId, followingUserName: userName }),
      setContextMenu: (menu) => set({ contextMenu: menu }),
      setHistoricalMode: (active, timestamp) =>
        set({ isHistoricalMode: active, historicalTimestamp: timestamp ?? null }),
      setHistoricalTimestamp: (timestamp) => set({ historicalTimestamp: timestamp }),
      // When engagedIncidentId is provided (including null), record which
      // incident was engaged when this viewport was persisted; when omitted,
      // the previous stamp is preserved. EXCEPTION: if the viewport being
      // persisted is the continental-US default, always stamp null — the
      // default must never masquerade as an intentional incident view, or
      // it would permanently satisfy incident-viewport precedence and block
      // engaged-incident centering on future map opens.
      setViewport: (viewport, engagedIncidentId) =>
        set((state) => {
          const merged = { ...state.viewport, ...viewport };
          const stamp: string | null | undefined = isDefaultViewport(merged)
            ? null
            : engagedIncidentId;
          return {
            viewport: merged,
            ...(stamp !== undefined ? { viewportIncidentId: stamp } : {}),
          };
        }),
      setBounds: (bounds) => set((state) => ({ viewport: { ...state.viewport, bounds } })),
      // Apply tenant default extent (only if viewport is still at default US center)
      applyTenantDefaultExtent: (centerLat, centerLng, zoom) =>
        set((state) => {
          const isAtDefault = isDefaultViewport(state.viewport);
          if (isAtDefault && centerLat !== undefined && centerLng !== undefined) {
            return { viewport: { ...state.viewport, center: [centerLng, centerLat], zoom: zoom ?? 10 } };
          }
          return state;
        }),
      setCoordinateFormat: (format) => set({ coordinateFormat: format }),
      toggleCoordinateFormat: () =>
        set((state) => ({ coordinateFormat: state.coordinateFormat === 'decimal' ? 'dms' : 'decimal' })),
      setClusterMode: (mode) => set({ clusterMode: mode }),
      toggleSpiderfier: () => set((state) => ({ isSpiderfierEnabled: !state.isSpiderfierEnabled })),
      toggleTerrain: () => set((state) => ({ isTerrainEnabled: !state.isTerrainEnabled })),
      setTerrainExaggeration: (exaggeration) =>
        set({ terrainExaggeration: Math.max(0.5, Math.min(3.0, exaggeration)) }),
      toggleBuildingsExtrusion: () => set((state) => ({ isBuildingsExtrusionEnabled: !state.isBuildingsExtrusionEnabled })),
      toggleDamageHeatmap: () => set((state) => ({ isDamageHeatmapVisible: !state.isDamageHeatmapVisible })),
      toggleResourceHeatmap: () => set((state) => ({ isResourceHeatmapVisible: !state.isResourceHeatmapVisible })),
      toggleTaskHeatmap: () => set((state) => ({ isTaskHeatmapVisible: !state.isTaskHeatmapVisible })),
      setHeatmapOpacity: (opacity) => set({ heatmapOpacity: Math.max(0, Math.min(1, opacity)) }),
      toggleRadarAnimation: () => set((state) => ({ isRadarAnimating: !state.isRadarAnimating })),
      setRadarCurrentFrame: (frame) => set({ radarCurrentFrame: frame }),
      resetUIState: () => set({ ...INITIAL_UI_STATE }),
    }),
    {
      name: 'map-ui-storage',
      version: 2,
      // v0 → v1: viewport gains a viewportIncidentId stamp (which incident was
      // engaged when the viewport was persisted). Old persisted viewports have
      // no stamp — treat as "no/unknown incident" (null).
      // v1 → v2: v1 had a bug where the debounced bounds writer stamped the
      // continental-US DEFAULT viewport with the engaged incident id, so the
      // stale stamp permanently satisfied incident-viewport precedence and the
      // map never centered the engaged incident again. Null the stamp (and
      // drop stale bounds) for any persisted viewport that is the default.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      migrate: (persistedState: any, version: number) => {
        let state = { ...(persistedState ?? {}) };
        if (version < 1) {
          state.viewportIncidentId = null;
        }
        if (version < 2) {
          const vp = state.viewport;
          if (
            vp &&
            Array.isArray(vp.center) &&
            typeof vp.center[0] === 'number' &&
            typeof vp.center[1] === 'number' &&
            typeof vp.zoom === 'number' &&
            isDefaultViewport(vp as ViewportState)
          ) {
            state = {
              ...state,
              viewportIncidentId: null,
              // Drop stale bounds captured at the default extent too.
              viewport: { center: [vp.center[0], vp.center[1]], zoom: vp.zoom },
            };
          }
        }
        return state;
      },
      partialize: (state) => ({
        viewport: state.viewport,
        viewportIncidentId: state.viewportIncidentId,
        coordinateFormat: state.coordinateFormat,
        clusterMode: state.clusterMode,
        isSpiderfierEnabled: state.isSpiderfierEnabled,
        isTerrainEnabled: state.isTerrainEnabled,
        terrainExaggeration: state.terrainExaggeration,
        isBuildingsExtrusionEnabled: state.isBuildingsExtrusionEnabled,
        heatmapOpacity: state.heatmapOpacity,
        hasCompletedMapTour: state.hasCompletedMapTour,
        isHighContrastMode: state.isHighContrastMode,
      }),
    }
  )
);
